export type RequestStatus = "new" | "in-progress" | "completed";
export type ReviewStatus = "pending" | "approved" | "flagged";
export type ProjectStatus = "pending" | "in-progress" | "completed";
export type ProjectTaskHealth = "on-track" | "delayed" | "completed";

export interface ProjectTaskProgress {
  id: string;
  moduleName: string;
  owner: string;
  plannedPercent: number;
  actualPercent: number;
  plannedEndDate: string;
  actualEndDate?: string;
  notes: string;
}

export interface AdminRequest {
  id: string;
  clientName: string;
  phone: string;
  projectType: string;
  message: string;
  createdAt: string;
  status: RequestStatus;
}

export interface ProjectImageRecord {
  id: string;
  title: string;
  category: string;
  imageUrl: string;
  description: string;
}

export interface ReviewRecord {
  id: string;
  customerName: string;
  project: string;
  rating: number;
  comment: string;
  source: string;
  status: ReviewStatus;
}

export interface ProjectRecord {
  id: string;
  title: string;
  owner: string;
  ownerEmail?: string;
  clientId?: string;
  clientPhone?: string;
  location: string;
  area: string;
  rateOfConstruction: string;
  description: string;
  status: ProjectStatus;
  contractFiles: string[];
  createdAt: string;
  progressTasks: ProjectTaskProgress[];
}

const todayIso = () => new Date().toISOString().slice(0, 10);

export const getTaskHealth = (task: ProjectTaskProgress, referenceDate = todayIso()): ProjectTaskHealth => {
  if (task.actualPercent >= 100) {
    return "completed";
  }

  if (task.actualEndDate && task.actualEndDate > task.plannedEndDate) {
    return "delayed";
  }

  if (referenceDate > task.plannedEndDate && task.actualPercent < task.plannedPercent) {
    return "delayed";
  }

  return "on-track";
};

export const getTaskDelayDays = (task: ProjectTaskProgress, referenceDate = todayIso()) => {
  const health = getTaskHealth(task, referenceDate);

  if (health !== "delayed") {
    return 0;
  }

  const baselineDate = task.actualEndDate && task.actualEndDate > task.plannedEndDate ? task.actualEndDate : referenceDate;
  const planned = new Date(task.plannedEndDate).getTime();
  const actual = new Date(baselineDate).getTime();

  if (Number.isNaN(planned) || Number.isNaN(actual)) {
    return 0;
  }

  return Math.max(1, Math.ceil((actual - planned) / (1000 * 60 * 60 * 24)));
};

export const getProjectProgressSummary = (project: ProjectRecord, referenceDate = todayIso()) => {
  const tasks = project.progressTasks;
  const totalTasks = tasks.length;
  const delayedTasks = tasks.filter((task) => getTaskHealth(task, referenceDate) === "delayed").length;
  const completedTasks = tasks.filter((task) => task.actualPercent >= 100).length;
  const averagePlanned = totalTasks > 0 ? Math.round(tasks.reduce((sum, task) => sum + task.plannedPercent, 0) / totalTasks) : 0;
  const averageActual = totalTasks > 0 ? Math.round(tasks.reduce((sum, task) => sum + task.actualPercent, 0) / totalTasks) : 0;

  return {
    totalTasks,
    delayedTasks,
    completedTasks,
    averagePlanned,
    averageActual,
  };
};

export const seedRequests: AdminRequest[] = [
  {
    id: "REQ-101",
    clientName: "Ravi Menon",
    phone: "+91 98765 11223",
    projectType: "Residential",
    message: "Need interior renovation for a 3BHK apartment in Chennai.",
    createdAt: "2026-03-12",
    status: "new",
  },
  {
    id: "REQ-102",
    clientName: "Ananya Sharma",
    phone: "+91 99881 77221",
    projectType: "Commercial",
    message: "Looking for office fit-out work for a startup workspace.",
    createdAt: "2026-03-11",
    status: "in-progress",
  },
  {
    id: "REQ-103",
    clientName: "S. Karthik",
    phone: "+91 94444 55510",
    projectType: "Maintenance",
    message: "Need recurring facility maintenance support for two sites.",
    createdAt: "2026-03-10",
    status: "completed",
  },
];

export const seedProjectImages: ProjectImageRecord[] = [
  {
    id: "IMG-201",
    title: "Luxury Villa Exterior",
    category: "Residential",
    imageUrl: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1200&q=80",
    description: "Completed facade and landscape handover for a premium villa project.",
  },
  {
    id: "IMG-202",
    title: "Corporate Office Floor",
    category: "Commercial",
    imageUrl: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200&q=80",
    description: "Open office fit-out with meeting zones and collaborative spaces.",
  },
  {
    id: "IMG-203",
    title: "Factory Setup Zone",
    category: "Industrial",
    imageUrl: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80",
    description: "Industrial workflow area delivered with storage and safety circulation.",
  },
];

export const seedReviews: ReviewRecord[] = [
  {
    id: "REV-301",
    customerName: "Pooja Iyer",
    project: "Home Renovation",
    rating: 5,
    comment: "Execution quality was strong and the site updates stayed consistent throughout the work.",
    source: "Website Video Review",
    status: "approved",
  },
  {
    id: "REV-302",
    customerName: "Rahul Verma",
    project: "Office Interior Setup",
    rating: 4,
    comment: "The team handled the office setup well, but we wanted faster final documentation.",
    source: "Website Comment",
    status: "pending",
  },
  {
    id: "REV-303",
    customerName: "Meera Nair",
    project: "Maintenance Contract",
    rating: 2,
    comment: "Response was delayed in one week and that needs internal follow-up.",
    source: "Manual Entry",
    status: "flagged",
  },
];

export const seedProjects: ProjectRecord[] = [
  {
    id: "PRJ-401",
    title: "Palm Residency Villa Block",
    owner: "Ravi Menon",
    location: "Lucknow",
    area: "3200 sq ft",
    rateOfConstruction: "INR 1850 / sq ft",
    description: "Premium residential villa project with interior finishing and landscape work.",
    status: "in-progress",
    contractFiles: ["palm-residency-contract.pdf"],
    createdAt: "2026-03-11",
    progressTasks: [
      {
        id: "TSK-401-1",
        moduleName: "Foundation",
        owner: "Civil Team",
        plannedPercent: 100,
        actualPercent: 100,
        plannedEndDate: "2026-03-05",
        actualEndDate: "2026-03-04",
        notes: "Foundation and footing completed ahead of schedule.",
      },
      {
        id: "TSK-401-2",
        moduleName: "Structure",
        owner: "Site Execution",
        plannedPercent: 75,
        actualPercent: 60,
        plannedEndDate: "2026-03-13",
        notes: "Steel delivery slipped by one day, impacting slab casting sequence.",
      },
      {
        id: "TSK-401-3",
        moduleName: "Electrical Rough-In",
        owner: "MEP Team",
        plannedPercent: 45,
        actualPercent: 45,
        plannedEndDate: "2026-03-18",
        notes: "Routing aligned with structural openings.",
      },
    ],
  },
  {
    id: "PRJ-402",
    title: "North Square Office Fit-out",
    owner: "Ananya Sharma",
    location: "Noida",
    area: "5400 sq ft",
    rateOfConstruction: "INR 2100 / sq ft",
    description: "Commercial office execution including partitions, lighting, HVAC coordination, and final handover.",
    status: "pending",
    contractFiles: ["north-square-work-order.pdf"],
    createdAt: "2026-03-09",
    progressTasks: [
      {
        id: "TSK-402-1",
        moduleName: "Design Freeze",
        owner: "Planning Cell",
        plannedPercent: 100,
        actualPercent: 100,
        plannedEndDate: "2026-03-08",
        actualEndDate: "2026-03-08",
        notes: "Client approvals complete.",
      },
      {
        id: "TSK-402-2",
        moduleName: "Procurement",
        owner: "Purchase Team",
        plannedPercent: 50,
        actualPercent: 30,
        plannedEndDate: "2026-03-12",
        notes: "Lighting vendor PO delayed due to revised BOQ.",
      },
      {
        id: "TSK-402-3",
        moduleName: "Partition Setup",
        owner: "Interior Team",
        plannedPercent: 20,
        actualPercent: 20,
        plannedEndDate: "2026-03-19",
        notes: "Site mobilization complete.",
      },
    ],
  },
  {
    id: "PRJ-403",
    title: "Delta Industrial Shed",
    owner: "S. Karthik",
    location: "Kanpur",
    area: "12000 sq ft",
    rateOfConstruction: "INR 1450 / sq ft",
    description: "Industrial shed with fabrication, flooring, drainage, and utility routing completed.",
    status: "completed",
    contractFiles: ["delta-industrial-contract.pdf", "delta-industrial-annexure.pdf"],
    createdAt: "2026-03-05",
    progressTasks: [
      {
        id: "TSK-403-1",
        moduleName: "Fabrication",
        owner: "Factory Team",
        plannedPercent: 100,
        actualPercent: 100,
        plannedEndDate: "2026-03-01",
        actualEndDate: "2026-02-28",
        notes: "All columns and trusses installed.",
      },
      {
        id: "TSK-403-2",
        moduleName: "Flooring",
        owner: "Civil Team",
        plannedPercent: 100,
        actualPercent: 100,
        plannedEndDate: "2026-03-03",
        actualEndDate: "2026-03-03",
        notes: "Surface finishing delivered as planned.",
      },
      {
        id: "TSK-403-3",
        moduleName: "Utilities",
        owner: "MEP Team",
        plannedPercent: 100,
        actualPercent: 100,
        plannedEndDate: "2026-03-04",
        actualEndDate: "2026-03-05",
        notes: "Testing completed one day later with no commercial impact.",
      },
    ],
  },
];

